import React, { useState } from 'react';
import { X, Search, Loader2, AlertCircle, FileText, CheckCircle, ChevronDown, Crown, Star, Award, Gem, Settings } from 'lucide-react';
import { SolicitarRelatorioResponse, ConsultationProductType, TipoConsultaRelatorio, TIPO_CONSULTA_INFO, INFORMACOES_ADICIONAIS_OPTIONS } from '../types';
import { solicitarRelatorio } from '../services/consultationService';

interface SolicitarRelatorioModalProps {
  isOpen: boolean;
  onClose: () => void;
  product: ConsultationProductType;
  userCredits: number;
  onSuccess: (result: SolicitarRelatorioResponse) => void;
  onInsufficientCredits: () => void;
}

const SolicitarRelatorioModal: React.FC<SolicitarRelatorioModalProps> = ({
  isOpen,
  onClose,
  product,
  userCredits,
  onSuccess,
  onInsufficientCredits,
}) => {
  const [plate, setPlate] = useState('');
  const [tipoConsulta, setTipoConsulta] = useState<TipoConsultaRelatorio>('ouro');
  const [informacoesAdicionais, setInformacoesAdicionais] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [showTipoDropdown, setShowTipoDropdown] = useState(false);

  const formatPlate = (value: string): string => {
    const clean = value.toUpperCase().replace(/[^A-Z0-9]/g, '');
    if (clean.length <= 3) return clean;
    if (clean.length <= 7) return clean.slice(0, 3) + '-' + clean.slice(3);
    return clean.slice(0, 3) + '-' + clean.slice(3, 7);
  };

  const handlePlateChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const formatted = formatPlate(e.target.value);
    if (formatted.length <= 8) {
      setPlate(formatted);
      setError('');
    }
  };

  const validatePlate = (plate: string): boolean => {
    const clean = plate.replace(/[^A-Z0-9]/g, '');
    return /^[A-Z]{3}[0-9][A-Z0-9][0-9]{2}$/.test(clean);
  };

  const toggleInfoAdicional = (id: string) => {
    setInformacoesAdicionais(prev => 
      prev.includes(id) 
        ? prev.filter(i => i !== id)
        : [...prev, id]
    );
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (userCredits < product.creditCost) {
      onInsufficientCredits();
      return;
    }

    if (!validatePlate(plate)) {
      setError('Formato de placa inválido. Use ABC1234 ou ABC1D23.');
      return;
    }

    if (tipoConsulta === 'personalizada' && informacoesAdicionais.length === 0) {
      setError('Selecione ao menos uma informação adicional para consulta personalizada.');
      return;
    }

    setLoading(true);

    try {
      const result = await solicitarRelatorio(
        plate, 
        tipoConsulta,
        tipoConsulta === 'personalizada' ? informacoesAdicionais : undefined
      );
      onSuccess(result);
      setPlate('');
      setTipoConsulta('ouro');
      setInformacoesAdicionais([]);
    } catch (err) {
      const error = err as Error;
      setError(error.message || 'Erro ao solicitar relatório. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setPlate('');
      setError('');
      setTipoConsulta('ouro');
      setInformacoesAdicionais([]);
      onClose();
    }
  };

  const getTipoIcon = (tipo: TipoConsultaRelatorio) => {
    switch (tipo) {
      case 'bronze': return <Award className="w-5 h-5 text-amber-600" />;
      case 'prata': return <Star className="w-5 h-5 text-slate-400" />;
      case 'ouro': return <Crown className="w-5 h-5 text-yellow-500" />;
      case 'diamante': return <Gem className="w-5 h-5 text-cyan-400" />;
      case 'personalizada': return <Settings className="w-5 h-5 text-purple-400" />;
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      {/* Backdrop */}
      <div 
        className="absolute inset-0 bg-black/80 backdrop-blur-sm"
        onClick={handleClose}
      />
      
      {/* Modal */}
      <div className="relative w-full max-w-2xl bg-gradient-to-b from-slate-900 to-slate-950 rounded-3xl border border-slate-700/50 shadow-2xl overflow-hidden max-h-[90vh] overflow-y-auto">
        {/* Gradient top border */}
        <div className="absolute top-0 left-0 right-0 h-1 bg-gradient-to-r from-purple-500 via-pink-500 to-rose-500" />
        
        {/* Header */}
        <div className="p-6 border-b border-slate-800/50 sticky top-0 bg-slate-900/95 backdrop-blur-sm z-10">
          <div className="flex items-start justify-between">
            <div className="flex items-center gap-4">
              <div className="p-3 bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl border border-purple-500/30">
                <FileText className="w-6 h-6 text-purple-400" />
              </div>
              <div>
                <h2 className="text-xl font-bold text-white">{product.name}</h2>
                <p className="text-sm text-slate-400 mt-1">
                  Relatório completo em PDF com protocolo de acompanhamento
                </p>
              </div>
            </div>
            <button 
              onClick={handleClose}
              disabled={loading}
              className="p-2 hover:bg-slate-800 rounded-xl transition-colors text-slate-400 hover:text-white disabled:opacity-50"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          {/* Plate Input */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Search className="w-4 h-4 text-purple-400" />
              Placa do Veículo
            </label>
            <input
              type="text"
              value={plate}
              onChange={handlePlateChange}
              placeholder="ABC-1D23"
              className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-2xl py-4 px-6 text-white text-2xl font-mono tracking-[0.3em] uppercase text-center focus:outline-none focus:border-purple-500 focus:ring-2 focus:ring-purple-500/20 transition-all placeholder:text-slate-600"
              disabled={loading}
              autoFocus
            />
          </div>

          {/* Report Type Selector */}
          <div className="space-y-3">
            <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
              <Crown className="w-4 h-4 text-purple-400" />
              Tipo de Relatório
            </label>
            <div className="relative">
              <button
                type="button"
                onClick={() => setShowTipoDropdown(!showTipoDropdown)}
                className="w-full bg-slate-800/50 border-2 border-slate-700/50 rounded-xl py-3 px-4 text-left flex items-center justify-between hover:border-purple-500/50 transition-all"
              >
                <div className="flex items-center gap-3">
                  {getTipoIcon(tipoConsulta)}
                  <div>
                    <p className="font-medium text-white">{TIPO_CONSULTA_INFO[tipoConsulta].name}</p>
                    <p className="text-xs text-slate-400">{TIPO_CONSULTA_INFO[tipoConsulta].description}</p>
                  </div>
                </div>
                <ChevronDown className={`w-5 h-5 text-slate-400 transition-transform ${showTipoDropdown ? 'rotate-180' : ''}`} />
              </button>
              
              {showTipoDropdown && (
                <div className="absolute top-full left-0 right-0 mt-2 bg-slate-800 border border-slate-700 rounded-xl overflow-hidden z-20 shadow-xl">
                  {(Object.keys(TIPO_CONSULTA_INFO) as TipoConsultaRelatorio[]).map((tipo) => (
                    <button
                      key={tipo}
                      type="button"
                      onClick={() => {
                        setTipoConsulta(tipo);
                        setShowTipoDropdown(false);
                        if (tipo !== 'personalizada') {
                          setInformacoesAdicionais([]);
                        }
                      }}
                      className={`w-full p-3 flex items-center gap-3 hover:bg-slate-700/50 transition-colors ${
                        tipoConsulta === tipo ? 'bg-purple-500/20' : ''
                      }`}
                    >
                      {getTipoIcon(tipo)}
                      <div className="text-left">
                        <p className="font-medium text-white">{TIPO_CONSULTA_INFO[tipo].name}</p>
                        <p className="text-xs text-slate-400">{TIPO_CONSULTA_INFO[tipo].description}</p>
                      </div>
                    </button>
                  ))}
                </div>
              )}
            </div>
          </div>

          {/* Additional Info (for personalizada) */}
          {tipoConsulta === 'personalizada' && (
            <div className="space-y-3">
              <label className="text-sm font-medium text-slate-300 flex items-center gap-2">
                <Settings className="w-4 h-4 text-purple-400" />
                Informações Adicionais
                <span className="text-xs text-slate-500">(selecione ao menos uma)</span>
              </label>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 max-h-48 overflow-y-auto p-1">
                {Object.entries(INFORMACOES_ADICIONAIS_OPTIONS).map(([id, label]) => (
                  <button
                    key={id}
                    type="button"
                    onClick={() => toggleInfoAdicional(id)}
                    className={`p-3 rounded-lg text-left text-sm transition-all flex items-center gap-2 ${
                      informacoesAdicionais.includes(id)
                        ? 'bg-purple-500/20 border border-purple-500/50 text-purple-300'
                        : 'bg-slate-800/50 border border-slate-700/30 text-slate-400 hover:border-slate-600'
                    }`}
                  >
                    <div className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 ${
                      informacoesAdicionais.includes(id)
                        ? 'bg-purple-500 border-purple-500'
                        : 'border-slate-600'
                    }`}>
                      {informacoesAdicionais.includes(id) && (
                        <CheckCircle className="w-3 h-3 text-white" />
                      )}
                    </div>
                    <span className="truncate">{label}</span>
                  </button>
                ))}
              </div>
            </div>
          )}

          {/* Error */}
          {error && (
            <div className="flex items-center gap-3 text-red-400 text-sm bg-red-500/10 p-4 rounded-xl border border-red-500/20">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>{error}</span>
            </div>
          )}

          {/* Features */}
          <div className="p-4 bg-slate-800/30 rounded-2xl border border-slate-700/30">
            <p className="text-xs font-semibold text-slate-400 mb-3 uppercase tracking-wider">
              O relatório inclui:
            </p>
            <div className="grid grid-cols-2 gap-2">
              {product.features.map((feature, idx) => (
                <div key={idx} className="flex items-center gap-2 text-xs text-slate-300">
                  <CheckCircle className="w-3.5 h-3.5 text-purple-500 shrink-0" />
                  {feature}
                </div>
              ))}
            </div>
          </div>

          {/* Cost Info - Premium styling */}
          <div className="flex items-center justify-between p-4 bg-gradient-to-r from-purple-500/10 to-pink-500/10 rounded-2xl border border-purple-500/20">
            <div>
              <p className="text-sm text-purple-300">Custo da consulta</p>
              <p className="text-3xl font-bold bg-gradient-to-r from-purple-400 to-pink-400 bg-clip-text text-transparent">
                {product.creditCost} créditos
              </p>
            </div>
            <div className="text-right">
              <p className="text-sm text-slate-400">Seu saldo</p>
              <p className={`text-xl font-bold ${userCredits >= product.creditCost ? 'text-white' : 'text-red-400'}`}>
                {userCredits} créditos
              </p>
            </div>
          </div>

          {/* Warning if not enough credits */}
          {userCredits < product.creditCost && (
            <div className="flex items-center gap-3 text-amber-400 text-sm bg-amber-500/10 p-4 rounded-xl border border-amber-500/20">
              <AlertCircle className="w-5 h-5 shrink-0" />
              <span>Você precisa de mais {product.creditCost - userCredits} créditos para esta consulta.</span>
            </div>
          )}

          {/* Submit Button */}
          <button
            type="submit"
            disabled={loading || plate.length < 7 || userCredits < product.creditCost}
            className="w-full bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-500 hover:to-pink-500 disabled:from-slate-700 disabled:to-slate-700 disabled:cursor-not-allowed text-white font-bold py-4 rounded-2xl transition-all flex items-center justify-center gap-3 text-lg shadow-lg shadow-purple-500/20 disabled:shadow-none"
          >
            {loading ? (
              <>
                <Loader2 className="w-6 h-6 animate-spin" />
                Solicitando Relatório...
              </>
            ) : (
              <>
                <FileText className="w-6 h-6" />
                Solicitar Relatório
              </>
            )}
          </button>

          <p className="text-center text-xs text-slate-500">
            Você receberá um protocolo para acompanhar e baixar o PDF
          </p>
        </form>
      </div>
    </div>
  );
};

export default SolicitarRelatorioModal;

